// VansPanel — list, add, and edit vans
import { useEffect, useState } from 'react';
import { getVans, createVan, updateVan, getDrivers } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';

const EMPTY = { number_plate: '', capacity: 20, driver_id: '' };

export default function VansPanel() {
  const [vans,    setVans]    = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [form,    setForm]    = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [msg,     setMsg]     = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [v, d] = await Promise.all([getVans(), getDrivers()]);
      setVans(v); setDrivers(d);
    } catch { setMsg('Failed to load'); }
  }

  function startEdit(van) {
    setEditing(van.id);
    setForm({ number_plate: van.number_plate, capacity: van.capacity, driver_id: van.driver_id || '' });
  }

  function cancel() { setEditing(null); setForm(EMPTY); setMsg(''); }

  async function save(e) {
    e.preventDefault();
    const capacity = parseInt(form.capacity, 10);
    if (!Number.isInteger(capacity) || capacity < 1) {
      setMsg('Error: Capacity must be a whole number of 1 or more');
      return;
    }
    const payload = { ...form, capacity, driver_id: form.driver_id || null };
    try {
      editing ? await updateVan(editing, payload) : await createVan(payload);
      setMsg(editing ? 'Van updated ✅' : 'Van added ✅');
      cancel(); load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Vans</h2>

      <Card className="p-5 mb-6 max-w-[400px] flex flex-col gap-3">
        <h3 className="font-display font-bold text-[15px] text-ink m-0">{editing ? 'Edit Van' : 'Add Van'}</h3>
        <form onSubmit={save} className="flex flex-col gap-3">
          <label className={labelCls}>Number Plate
            <input className={inputCls} required value={form.number_plate} onChange={e => setForm({...form, number_plate: e.target.value})} />
          </label>
          <label className={labelCls}>Capacity
            <input type="number" min="1" className={inputCls} required value={form.capacity}
              onChange={e => setForm({...form, capacity: e.target.value})} />
          </label>
          <label className={labelCls}>Assign Driver
            <select className={inputCls} value={form.driver_id} onChange={e => setForm({...form, driver_id: e.target.value})}>
              <option value="">— None —</option>
              {drivers.map(d => <option key={d.id} value={d.id}>{d.name} ({d.phone})</option>)}
            </select>
          </label>
          <div className="flex gap-2">
            <Button type="submit">{editing ? 'Update' : 'Add'}</Button>
            {editing && <Button variant="secondary" type="button" onClick={cancel}>Cancel</Button>}
          </div>
          {msg && (
            <p className={`text-sm font-bold px-3 py-2 rounded-s inline-block w-fit ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
              {msg}
            </p>
          )}
        </form>
      </Card>

      <Card>
        <CardHeader title={`Vans (${vans.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Plate</th>
                <th className="px-4 py-2.5">Capacity</th><th className="px-4 py-2.5">Driver</th>
                <th className="px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vans.map(v => (
                <tr key={v.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3 text-ink-soft tabular-nums">{v.id}</td>
                  <td className="px-4 py-3 font-semibold text-ink">{v.number_plate}</td>
                  <td className="px-4 py-3 tabular-nums">{v.capacity}</td>
                  <td className="px-4 py-3">{v.driver_name || '—'}</td>
                  <td className="px-4 py-3"><Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => startEdit(v)}>Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
